import React, { useState } from 'react';
import { Download, ListChecks } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

const ALL = ['Draft', 'Pending Approval', 'Rejected', 'Issued', 'Partially Received', 'Received', 'Closed', 'Canceled', 'Void'];
const DEFAULT = ['Issued', 'Partially Received', 'Received', 'Closed', 'Canceled', 'Void'];
const monthStart = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`; };
const today = () => new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 10);

export const PoExportButton = ({ api }) => {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ date_from: monthStart(), date_to: today(), statuses: DEFAULT, date_format: 'dd/mm/yyyy' });
  const [seq, setSeq] = useState(null);
  const [busy, setBusy] = useState(false);
  const toggle = (s) => setF({ ...f, statuses: f.statuses.includes(s) ? f.statuses.filter(x => x !== s) : [...f.statuses, s] });

  const openIt = () => { setOpen(true); api().get('/purchase-orders/export/sequence-check').then(r => setSeq(r.data)).catch(() => setSeq(null)); };
  const download = async () => {
    if (!f.statuses.length) { toast.error('Pick at least one status'); return; }
    setBusy(true);
    try {
      const r = await api().get('/purchase-orders/export/quickbooks', { params: { ...f, statuses: f.statuses.join(',') }, responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a'); a.href = url; a.download = `purchase-orders-qbo_${f.date_from || 'all'}_${f.date_to || 'all'}.csv`; a.click(); URL.revokeObjectURL(url);
      toast.success('Export downloaded');
      setOpen(false);
    } catch (e) { toast.error('Export failed'); }
    finally { setBusy(false); }
  };

  return (
    <>
      <Button variant="outline" onClick={openIt} className="rounded-sm" data-testid="po-export-btn"><Download className="w-4 h-4 mr-1" /> Export for Accounting</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="po-export-dialog">
          <DialogHeader><DialogTitle>Export POs for QuickBooks Online</DialogTitle></DialogHeader>
          <div className="space-y-4 text-sm">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>PO date from</Label><Input type="date" value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value })} className="rounded-sm" data-testid="po-export-from" /></div>
              <div><Label>PO date to</Label><Input type="date" value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value })} className="rounded-sm" data-testid="po-export-to" /></div>
            </div>
            <div><Label>Statuses</Label>
              <div className="grid grid-cols-3 gap-1.5 mt-1">{ALL.map(s => (
                <label key={s} className="flex items-center gap-1.5 text-xs"><input type="checkbox" checked={f.statuses.includes(s)} onChange={() => toggle(s)} data-testid={`po-export-status-${s.toLowerCase().replace(/ /g, '-')}`} /> {s}</label>
              ))}</div>
            </div>
            <div><Label>Date format</Label>
              <select value={f.date_format} onChange={(e) => setF({ ...f, date_format: e.target.value })} className="w-full h-9 border border-slate-200 rounded-sm px-2 bg-white" data-testid="po-export-date-format">
                <option value="dd/mm/yyyy">DD/MM/YYYY (QuickBooks Online Canada)</option>
                <option value="mm/dd/yyyy">MM/DD/YYYY (QuickBooks Online US)</option>
                <option value="yyyy-mm-dd">YYYY-MM-DD</option>
              </select>
            </div>
            <p className="text-[11px] text-slate-500">One row per PO line (plus a Freight row when shipping is charged). Columns: PO No, Vendor, PO Date, Expected Date, Item/Account, Description, Qty, Rate, Amount, Tax Code, Memo, Status. Voided POs are listed with $0 so the numbering has no gaps.</p>
            {seq && (
              <div className={`text-xs px-3 py-2 rounded-sm border flex items-start gap-2 ${seq.gap_count ? 'bg-amber-50 border-amber-200 text-amber-900' : 'bg-emerald-50 border-emerald-200 text-emerald-900'}`} data-testid="po-sequence-check">
                <ListChecks className="w-4 h-4 shrink-0" />
                {seq.count === 0 ? 'No POs in the running sequence yet.' : seq.gap_count
                  ? <span>Sequence {seq.first}–{seq.last}: <b>{seq.gap_count} missing</b> ({seq.gaps.slice(0, 15).join(', ')}{seq.gap_count > 15 ? '…' : ''}). These were removed before numbers were protected.</span>
                  : <span>Sequence {seq.first}–{seq.last} is continuous, no gaps.</span>}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm" data-testid="po-export-cancel">Cancel</Button>
            <Button onClick={download} disabled={busy} className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="po-export-download"><Download className="w-3.5 h-3.5 mr-1" />{busy ? 'Exporting…' : 'Download CSV'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PoExportButton;
